import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { api } from '../services/api';
import Logo from '../components/Logo';
import { STORY_TYPES } from '../types/story';

interface Story {
  story_id: string;
  title: string;
  story_type: string;
  status: string;
  created_at: string;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, isAuthenticated, clearAuth } = useAuthStore();
  const [recentStories, setRecentStories] = useState<Story[]>([]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  // Fetch recent stories
  useEffect(() => {
    const fetchRecentStories = async () => {
      try {
        const data = await api.getStories();
        // Get the 10 most recent stories
        const stories = (data as unknown as Story[])
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
          .slice(0, 10);
        setRecentStories(stories);
      } catch (error) {
        console.error('Error fetching stories:', error);
      }
    };

    if (isAuthenticated) {
      fetchRecentStories();
    }
  }, [isAuthenticated]);

  const handleLogout = async () => {
    const refreshToken = useAuthStore.getState().refreshToken;
    if (refreshToken) {
      try {
        await api.logout(refreshToken);
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    clearAuth();
    navigate('/');
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
            ✓ Ready
          </span>
        );
      case 'generating':
        return (
          <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">
            Generating
          </span>
        );
      case 'failed':
        return (
          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
            Failed
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
            {status}
          </span>
        );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background-cream">
      {/* Header */}
      <header className="bg-white shadow-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Logo size="md" />
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-primary-500">Logged in as</p>
              <p className="text-emerald-600 font-semibold">{user.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="btn-outline px-4 py-2 text-sm"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-3xl p-8 text-white mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-4xl font-display font-bold mb-2">
                Welcome back! 🌱
              </h2>
              <p className="text-lg opacity-90">
                Ready to create magical stories for your children?
              </p>
            </div>
            <div className="text-right">
              <p className="text-5xl font-display font-bold">
                {user.credit_balance}
              </p>
              <p className="text-sm opacity-80 mt-1">
                {user.credit_balance === 1 ? 'credit' : 'credits'}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content - 2 Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 mb-8">
          {/* Create Story Initiator - 30% */}
          <div className="lg:col-span-3">
            <div className="card-hover h-full">
              <div className="text-center">
                <div className="text-6xl mb-4">✨</div>
                <h3 className="text-2xl font-display font-bold text-emerald-500 mb-3">
                  Create New Story
                </h3>
                <p className="text-gray-600 text-sm mb-6">
                  Start crafting a personalized audiobook for your child
                </p>
                <button
                  onClick={() => navigate('/create-story')}
                  className="btn-primary w-full"
                >
                  Create Story
                </button>
              </div>
            </div>
          </div>

          {/* Audiobook Library - 70% */}
          <div className="lg:col-span-7">
            <div className="card h-full">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-display font-bold text-emerald-500">
                  Recent Stories
                </h3>
                <button
                  onClick={() => navigate('/stories')}
                  className="text-primary-500 hover:text-primary-600 font-semibold text-sm"
                >
                  View All →
                </button>
              </div>

              {recentStories.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">📚</div>
                  <p className="text-gray-700 text-lg mb-2">
                    No stories yet
                  </p>
                  <p className="text-gray-600 text-sm">
                    Create your first personalized audiobook to get started!
                  </p>
                </div>
              ) : (
                <div className="max-h-96 overflow-y-auto rounded-xl border border-gray-200">
                  <table className="w-full">
                    <thead className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white sticky top-0">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Story</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold">Created</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {recentStories.map((story) => {
                        const storyType = STORY_TYPES.find((t) => t.value === story.story_type);

                        return (
                          <tr
                            key={story.story_id}
                            className="hover:bg-primary-50/50 transition-colors cursor-pointer"
                            onClick={() => navigate(`/stories/${story.story_id}`)}
                          >
                            {/* Story Title */}
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="text-xl">{storyType?.emoji}</span>
                                <div className="min-w-0">
                                  <p className="font-semibold text-primary-600 text-sm truncate">
                                    {story.title}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    {storyType?.label}
                                  </p>
                                </div>
                              </div>
                            </td>

                            {/* Status */}
                            <td className="px-4 py-3">
                              {getStatusBadge(story.status)}
                            </td>

                            {/* Created Date */}
                            <td className="px-4 py-3">
                              <span className="text-xs text-gray-500">
                                {formatDate(story.created_at)}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Coming Soon Notice */}
        <div className="mt-8 p-6 bg-blue-50 border-2 border-blue-200 rounded-xl">
          <p className="text-blue-800 font-semibold">
            🚀 This dashboard is a work in progress! More features coming soon.
          </p>
        </div>
      </main>
    </div>
  );
}
